/**
 * ScrTracksFilter — панель слоя «Треки исполнителей» (DA-14933).
 * Режим суток: контрол выключения отдельных ролей исполнителя (как у «Парковки с радиусом»).
 * Режим деталей смены (выбрана смена): группа ролей скрыта; вместо неё — кнопка возврата
 * к слою всех треков и легенда элементов трека (маршрут, ключевые точки).
 * Выбор даты вынесен в таймлайн-оверлей (ScrTracksTimeline), здесь его нет.
 */
import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import Popper from '@material-ui/core/Popper';
import TextField from '@material-ui/core/TextField';
import * as T from '../../../constants/types';
import { SCR_TRACK_ROLES_FILTER, SCR_INFRA_FILTER } from '../../../constants/filterStyles';
import { SCR_TRACKS_STYLE } from '../../../constants/layerStyles';
import { getStyleFilterItems } from '../../../constants/filterStylesResolver';
import { onChangeScrTrackRolesFilter, onChangeScrInfraFilter, setScrTracksSelectedExecutor } from '../../../ac/onChangeMapFilters';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import styles from '../FilterSidebar.module.css';

const { style, styleKey, reduxPath } = SCR_TRACK_ROLES_FILTER;
// Иконки дропдауна исполнителей — белые: поле тёмное, дефолтный серый на нём не читается.
const execCloseIcon = <CloseIcon className={styles.scrExecIcon} />;
const execPopupIcon = <ExpandMoreIcon className={styles.scrExecIcon} />;
// Цвет выбранной смены — по роли (фолбэк нейтральный). Совпадает с цветом трека/точек на карте.
const roleColor = role => {
    const found = SCR_TRACKS_STYLE.roles.find(r => r.value === role);
    return found ? found.color : '#757575';
};
const ScrTracksFilterComponent = ({
    mapFilters, onChangeScrTrackRolesFilter, onChangeScrInfraFilter,
    scrTracksSelectedShiftId, scrTracksSelectedRole, clearSelectedShift,
    scrTracksSelectedName, scrTracksSelectedPeriod, scrTracksSelectedRoleLabel,
    setScrTracksSelectedExecutor,
}) => {
    // Состояние поля поиска дропдауна исполнителей (хуки — до early-return режима деталей).
    const [ execSearch, setExecSearch ] = useState('');
    const [ execFocused, setExecFocused ] = useState(false);
    const detailsMode = scrTracksSelectedShiftId !== null && typeof scrTracksSelectedShiftId !== 'undefined';

    // Режим деталей смены: вместо тоглов ролей — кнопка «Назад» и легенда элементов трека.
    if (detailsMode) {
        const color = roleColor(scrTracksSelectedRole);
        return (
            <div style={{ paddingLeft: 10, paddingRight: 10, marginTop: 10 }}>
                <button
                    type="button"
                    className={styles.scrBackRow}
                    onClick={clearSelectedShift}
                    title={t`Назад к слою всех треков`}
                >
                    ‹ {t`Назад к слою всех треков`}
                </button>
                <div style={{ margin: '14px 0', fontSize: '1.05em', lineHeight: 1.9 }}>
                    <div><strong>{scrTracksSelectedName || '-'}</strong></div>
                    <div>{t`Смена`}: {scrTracksSelectedShiftId}</div>
                    {scrTracksSelectedPeriod ? <div>{t`Период`}: {scrTracksSelectedPeriod}</div> : null}
                    {scrTracksSelectedRoleLabel ? <div>{t`Роль`}: {scrTracksSelectedRoleLabel}</div> : null}
                </div>
                <div className={styles.scrLegendRow}>
                    <span style={{ display: 'inline-block', width: 24, height: 5, borderRadius: 3, background: color }} />
                    <span>{t`Маршрут исполнителя`}</span>
                </div>
                <div className={styles.scrLegendRow}>
                    <span style={{ justifySelf: 'center', display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: color, border: '2.5px solid rgba(43,43,43,0.5)' }} />
                    <span>{t`Ключевые точки`}</span>
                </div>
                <div style={{ marginBottom: 16 }} />
            </div>
        );
    }

    const items = getStyleFilterItems(style, styleKey);
    const enabled = mapFilters[reduxPath] || [];
    const infraItems = getStyleFilterItems(SCR_INFRA_FILTER.style, SCR_INFRA_FILTER.styleKey);
    const infraEnabled = mapFilters[SCR_INFRA_FILTER.reduxPath] || [];
    // Дропдаун исполнителей: список подтягивается из загруженных векторных тайлов (видимая область)
    // и сужается текущим фильтром ролей — в списке только те, чьи треки реально видны на карте.
    const allExecutors = mapFilters.scrTracksAvailableExecutors || [];
    const selectedExecutor = mapFilters.scrTracksSelectedExecutor;
    const executors = allExecutors.filter(ex => !ex.role || enabled.includes(ex.role));
    // Выбранный исполнитель мог уехать за пределы загруженных тайлов — добавим его в список,
    // чтобы контрол не сбрасывал значение (фильтр остаётся активным).
    const executorOptions = selectedExecutor && !executors.some(ex => ex.id === String(selectedExecutor))
        ? [{ id: String(selectedExecutor), name: String(scrTracksSelectedName || selectedExecutor) }, ...executors ]
        : executors;
    const selectedOption = executorOptions.find(ex => ex.id === String(selectedExecutor)) || null;
    const execFilterOptions = createFilterOptions({ stringify: option => option.name || '' });
    return (
        <div style={{ paddingLeft: 10, paddingRight: 10, marginTop: 10 }}>
            <Autocomplete
                options={executorOptions}
                getOptionLabel={option => (option && option.name) || ''}
                getOptionSelected={(option, value) => option.id === value.id}
                value={selectedOption}
                onChange={(e, value) => {
                    setScrTracksSelectedExecutor(value ? value.id : null);
                    setExecSearch('');
                    setExecFocused(false);
                    setTimeout(() => {
                        if (document.activeElement instanceof HTMLElement) {
                            document.activeElement.blur();
                        }
                    }, 0);
                }}
                inputValue={execFocused ? execSearch : ((selectedOption && selectedOption.name) || '')}
                onInputChange={(e, value, reason) => {
                    if (reason !== 'reset') setExecSearch(value);
                }}
                filterOptions={execFilterOptions}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder={execFocused ? t`Поиск исполнителя...` : t`Все исполнители`}
                        variant="standard"
                        onFocus={() => setExecFocused(true)}
                        onBlur={() => { setExecFocused(false); setExecSearch('') }}
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            style: {
                                color: '#fff',
                                background: 'rgba(46, 53, 61, 0.95)',
                                borderRadius: 6,
                                // Справа резервируем место под крестик + стрелку: свой padding
                                // перекрывает штатный отступ MUI, иначе текст лезет под иконки.
                                padding: '6px 58px 6px 10px',
                                // 16px явно: в легенде базовый шрифт 12px, а у дропдауна выбора
                                // города — 16px; иначе поле получается ниже эталонного.
                                fontSize: '16px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                            },
                        }}
                        inputProps={{
                            ...params.inputProps,
                            // Длинное ФИО обрезаем многоточием, чтобы текст не уезжал под
                            // крестик сброса и стрелку раскрытия (они справа поверх поля).
                            style: {
                                color: '#fff',
                                background: 'transparent',
                                fontSize: '16px',
                                padding: 0,
                                textOverflow: 'ellipsis',
                            },
                        }}
                    />
                )}
                // Отступы по 10px — чтобы поле выровнялось с legHead и checkboxIconGrid,
                // у которых внутри контейнера легенды свой padding 10px.
                style={{ width: 'calc(100% - 20px)', margin: '0 10px 12px' }}
                autoHighlight
                popupIcon={execPopupIcon}
                closeIcon={execCloseIcon}
                PopperComponent={props => (
                    <Popper
                        {...props}
                        style={{ ...props.style, zIndex: 2000, width: 'max-content', minWidth: props.style?.width ?? 240, maxWidth: 500 }}
                        placement="bottom-start"
                    />
                )}
                ListboxProps={{
                    style: {
                        background: '#fff',
                        color: '#212529',
                        borderRadius: 6,
                        fontSize: '0.95em',
                        maxHeight: 400,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                    },
                }}
            />
            <div className={styles.legHead}>{t`Роли исполнителей:`}</div>
            <div className={styles.checkboxIconGrid}>
                {items.map(({ value, color, label }) => (
                    <Fragment key={value}>
                        <span style={{ display: 'inline-block', width: '2vw', height: 4, borderRadius: 2, background: color, opacity: 0.85, alignSelf: 'center' }} />
                        <label htmlFor={value}>{i18n._(label)}</label>
                        <div>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    name="scr-role"
                                    id={value}
                                    checked={enabled.includes(value)}
                                    value={value}
                                    onChange={onChangeScrTrackRolesFilter}
                                />
                                <span className={classnames(styles.sliderRound, styles.slider)} />
                            </label>
                        </div>
                    </Fragment>
                ))}
            </div>
            <div className={styles.legHead}>{t`Инфраструктура:`}</div>
            <div className={styles.checkboxIconGrid}>
                {infraItems.map(({ value, color, label }) => (
                    <Fragment key={value}>
                        <span style={{ justifySelf: 'start', display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: color, border: '1.5px solid #fff' }} />
                        <label htmlFor={`infra-${value}`}>{i18n._(label)}</label>
                        <div>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    name="scr-infra"
                                    id={`infra-${value}`}
                                    checked={infraEnabled.includes(value)}
                                    value={value}
                                    onChange={onChangeScrInfraFilter}
                                />
                                <span className={classnames(styles.sliderRound, styles.slider)} />
                            </label>
                        </div>
                    </Fragment>
                ))}
            </div>
            <div style={{ marginBottom: 16 }} />
        </div>
    );
};
const mapStateToProps = ({ mapFilters, map: {
    scrTracksSelectedShiftId, scrTracksSelectedRole,
    scrTracksSelectedName, scrTracksSelectedPeriod, scrTracksSelectedRoleLabel,
} }) => ({
    mapFilters, scrTracksSelectedShiftId, scrTracksSelectedRole,
    scrTracksSelectedName, scrTracksSelectedPeriod, scrTracksSelectedRoleLabel,
});
const mapDispatchToProps = dispatch => ({
    onChangeScrTrackRolesFilter: e => dispatch(onChangeScrTrackRolesFilter(e)),
    onChangeScrInfraFilter: e => dispatch(onChangeScrInfraFilter(e)),
    setScrTracksSelectedExecutor: id => dispatch(setScrTracksSelectedExecutor(id)),
    clearSelectedShift: () => dispatch({
        type: T.SET_SCR_TRACKS_SELECTED_SHIFT,
        payload: { scrTracksSelectedShiftId: null, scrTracksSelectedRole: null },
    }),
});
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(ScrTracksFilterComponent);

export const ScrTracksFilter = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Треки исполнителей`
);
